/* global Alpine, google */

import { GoogleMapsRoute } from './google-maps'

// Salva il componente mappa fuori da Alpine per evitare problemi con i Proxy
let mapInstance = null

export default () => {
  document.addEventListener('alpine:init', () => {
    Alpine.data('bookingSearch', () => ({
      // Campi input (stringhe visualizzate)
      from: '',
      to: '',

      // Oggetti location completi (con id, name, lat, lng, etc.)
      from_loc: null,
      to_loc: null,

      // Altri campi form
      date: '',
      time: '',
      passengers: '1 Passeggero',
      luggage: 'Nessuno',
      childseats: '0',
      pets: '0',

      // Accordion campi extra (seggiolini/animali)
      extrasOpen: false,

      // Autocomplete
      suggestions: [],
      showSuggestions: false,
      activeField: null, // 'from' o 'to'

      // Route info (per visualizzazione)
      routeInfo: null,

      // Mappa a schermo intero
      mapFullscreen: false,

      // Ricerche recenti (da localStorage)
      recentSearches: [],

      isSubmitting: false,
      _searchTimer: null,
      _searchController: null,

      /** Minuti di anticipo minimi rispetto al pickup (da data-min-minutes-before-pickup, default 30) */
      minMinutesBeforePickup: 30,

      /** Prefisso lingua per i link (da data-lang-prefix, es. '/' o '/en/') */
      langPrefix: '/',

      init() {
        const val = this.$el.getAttribute('data-min-minutes-before-pickup')
        if (val !== null && val !== '') {
          const n = parseInt(val, 10)
          if (!Number.isNaN(n) && n >= 0) this.minMinutesBeforePickup = n
        }
        const prefix = this.$el.getAttribute('data-lang-prefix')
        if (prefix) this.langPrefix = prefix
        if (!this.date) {
          this.date = new Date().toISOString().split('T')[0]
        }

        this.loadRecentSearches()
      },

      initMap() {
        const mapElement = document.getElementById('booking-map')
        if (!mapElement) return

        // Salva riferimento a this per il callback
        const self = this

        mapInstance = new GoogleMapsRoute('booking-map', {
          draggableMarkers: true,
          onRouteCalculated: (info) => {
            self.routeInfo = info
          },
          onMarkerMoved: (type, lat, lng) => {
            self.handleMarkerMoved(type, lat, lng)
          },
        })
      },

      /**
       * Gestisce il riposizionamento manuale di un marker (partenza/arrivo).
       * 1) Se il punto è fuori dall'area servita: avvisa e riporta il marker indietro (nessuna chiamata API).
       * 2) Altrimenti reverse geocoding: se non c'è un indirizzo (mare/area vuota) avvisa e riporta indietro;
       *    in caso positivo aggiorna coordinate + indirizzo della località.
       */
      async handleMarkerMoved(type, lat, lng) {
        const locKey = type + '_loc'
        const current = this[locKey]
        if (!current) return

        // Annulla il drop riportando il marker dove si trovava all'inizio del trascinamento
        const revert = () => {
          if (mapInstance) mapInstance.revertMarker(type)
        }

        // 1) Controllo area servita (gratis, nessuna API)
        const bounds = this._parseMbounds()
        if (
          bounds &&
          (lat > bounds.north ||
            lat < bounds.south ||
            lng > bounds.east ||
            lng < bounds.west)
        ) {
          alert(
            "Questo punto è fuori dall'area servita. Spostalo all'interno dell'isola d'Ischia."
          )
          revert()
          return
        }

        // 2) Reverse geocoding del punto rilasciato
        let results
        try {
          results = await this._reverseGeocode(lat, lng)
        } catch (err) {
          // Verifica non eseguibile (es. Geocoding API non attiva o limite query):
          // non accettare un punto non validato, avvisa e riporta il marker indietro.
          console.error('Reverse geocoding error:', err)
          alert(
            'Non è stato possibile verificare questo punto. Riprova tra qualche istante.'
          )
          revert()
          return
        }

        const match = this._findServiceableResult(results, lat, lng)
        if (!match) {
          // Mare / specchio d'acqua / area senza una via raggiungibile
          alert(
            'Questo punto non sembra raggiungibile su strada (es. in mare o in un\u2019area senza indirizzo). Scegli una posizione più vicina a una via.'
          )
          revert()
          return
        }

        // Drop valido: il punto riposizionato diventa il luogo scelto.
        // Aggancia il marker all'indirizzo trovato (coordinate del risultato, non del drop),
        // scrive l'indirizzo nel campo e imposta l'id (place_id Google con prefisso 'g:')
        // che verrà usato come from_id/to_id nella ricerca.
        const matchLat = match.geometry.location.lat()
        const matchLng = match.geometry.location.lng()
        const address = match.formatted_address
        const placeId = match.place_id ? 'g:' + match.place_id : current.id
        this[locKey] = {
          ...current,
          id: placeId,
          name: address,
          lat: matchLat,
          lng: matchLng,
          address,
          customized: true,
        }
        this[type] = address

        // Se il percorso è già tracciato (entrambi i punti presenti), ricalcolalo
        // così la linea segue la nuova posizione del pin; altrimenti aggancia
        // semplicemente il marker all'indirizzo trovato.
        if (mapInstance) {
          if (this.from_loc && this.to_loc) {
            this.updateMap()
          } else {
            mapInstance.setMarkerPosition(type, matchLat, matchLng)
          }
        }
      },

      /**
       * Reverse geocoding via Google Maps JS (nessun costo server aggiuntivo).
       * @returns {Promise<Array>} array di risultati ([] se ZERO_RESULTS); rigetta su errore API
       */
      _reverseGeocode(lat, lng) {
        return new Promise((resolve, reject) => {
          if (typeof google === 'undefined' || !google.maps) {
            reject(new Error('Google Maps non disponibile'))
            return
          }
          const geocoder = new google.maps.Geocoder()
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK') {
              resolve(results || [])
            } else if (status === 'ZERO_RESULTS') {
              resolve([])
            } else {
              reject(new Error('Geocoder status: ' + status))
            }
          })
        })
      },

      /**
       * Restituisce il primo risultato del reverse geocoding "servibile": un indirizzo
       * PRECISO (location_type ROOFTOP o RANGE_INTERPOLATED, cioè ancorato a un edificio o
       * a una strada) entro pochi metri dal punto rilasciato. In mare o nei bacini portuali
       * Google àncora al massimo a una strada/POI lontani: la distanza supera la soglia e
       * non viene restituito alcun risultato (null).
       * ponytail: soglia fissa a 150 m (ceiling: punti validi molto isolati senza un
       * indirizzo preciso vicino verrebbero respinti; upgrade = poligono dell'isola).
       */
      _findServiceableResult(results, lat, lng) {
        if (!Array.isArray(results) || results.length === 0) return null
        const PRECISE = ['ROOFTOP', 'RANGE_INTERPOLATED']
        const MAX_METERS = 150
        return (
          results.find((r) => {
            const g = r.geometry
            if (!g || !g.location || !PRECISE.includes(g.location_type)) {
              return false
            }
            const d = this._haversineMeters(
              lat,
              lng,
              g.location.lat(),
              g.location.lng()
            )
            return d <= MAX_METERS
          }) || null
        )
      },

      /** Distanza in metri tra due coordinate (formula di Haversine) */
      _haversineMeters(lat1, lng1, lat2, lng2) {
        const R = 6371000
        const toRad = (d) => (d * Math.PI) / 180
        const dLat = toRad(lat2 - lat1)
        const dLng = toRad(lng2 - lng1)
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(toRad(lat1)) *
            Math.cos(toRad(lat2)) *
            Math.sin(dLng / 2) *
            Math.sin(dLng / 2)
        return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      },

      // Gestione Input e Autocomplete
      handleInput(field, value) {
        this[field] = value
        this.activeField = field

        if (field === 'from' && this.from_loc && this.from_loc.name !== value) {
          this.from_loc = null
        }
        if (field === 'to' && this.to_loc && this.to_loc.name !== value) {
          this.to_loc = null
        }

        if (value.length < 2) {
          this.suggestions = []
          this.showSuggestions = false
          return
        }

        clearTimeout(this._searchTimer)
        this._searchTimer = setTimeout(() => this._fetchLocations(value), 300)
      },

      async _fetchLocations(value) {
        if (this._searchController) this._searchController.abort()
        this._searchController = new AbortController()

        try {
          const response = await fetch(
            `/api?endpoint=locations&q=${encodeURIComponent(value)}`,
            { signal: this._searchController.signal }
          )
          const data = await response.json()
          this.suggestions = data
        } catch (error) {
          if (error.name === 'AbortError') return
          console.error('Error fetching locations:', error)
          this.suggestions = []
        }

        this.showSuggestions = this.suggestions.length > 0
      },

      selectSuggestion(place) {
        if (this.activeField) {
          this[this.activeField + '_loc'] = place
          this[this.activeField] = place.name
        }
        this.showSuggestions = false
        this.suggestions = []
        this.activeField = null

        this.updateMap()
      },

      swap() {
        // Scambia oggetti location
        const tempLoc = this.from_loc
        this.from_loc = this.to_loc
        this.to_loc = tempLoc

        // Scambia stringhe visualizzate
        const tempName = this.from
        this.from = this.to
        this.to = tempName

        // Aggiorna la mappa
        this.updateMap()
      },

      isValid() {
        return this.from_loc && this.to_loc && this.date && this.time
      },

      /** Bagagli selezionati (0 quando "Nessuno/solo a mano") */
      luggageCount() {
        if (
          !this.luggage ||
          this.luggage === '0' ||
          this.luggage === 'Nessuno'
        ) {
          return 0
        }
        return parseInt(this.luggage, 10) || 0
      },

      /** Numero totale di extra selezionati (bagagli + seggiolini + animali) */
      extrasCount() {
        return (
          this.luggageCount() +
          (parseInt(this.childseats, 10) || 0) +
          (parseInt(this.pets, 10) || 0)
        )
      },

      hasExtras() {
        return this.extrasCount() > 0
      },

      /** Data minima (oggi) per l'attributo min del campo data */
      minDateToday() {
        return new Date().toISOString().split('T')[0]
      },

      /** Ora minima quando la data è oggi: almeno minMinutesBeforePickup min da adesso (arrotondata ai 30 min) */
      minTimeForPickup() {
        const today = new Date().toISOString().split('T')[0]
        if (!this.date || this.date !== today) return '00:00'
        const minMs = this.minMinutesBeforePickup * 60 * 1000
        const d = new Date(Date.now() + minMs)
        d.setMinutes(Math.ceil(d.getMinutes() / 30) * 30)
        d.setSeconds(0, 0)
        return d.toTimeString().slice(0, 5)
      },

      /** Verifica che data/ora siano validi (non passato, oggi almeno +minMinutesBeforePickup min) */
      isDateTimeValid() {
        const today = new Date().toISOString().split('T')[0]
        if (this.date < today) return false
        if (this.date !== today) return true
        if (!this.time) return false
        const pickup = new Date(this.date + 'T' + this.time + ':00')
        const minPickup = new Date(
          Date.now() + this.minMinutesBeforePickup * 60 * 1000
        )
        return pickup.getTime() >= minPickup.getTime()
      },

      handleSubmit() {
        if (this.isSubmitting) return
        if (!this.isValid()) return
        const today = new Date().toISOString().split('T')[0]
        if (this.date < today) {
          alert('La data non può essere nel passato.')
          return
        }
        if (!this.isDateTimeValid()) {
          alert(
            "Il transfer deve essere prenotato almeno 30 minuti prima dell'orario di pickup."
          )
          return
        }
        this.isSubmitting = true
        const params = new URLSearchParams({
          from_id: this.from_loc?.id ?? '',
          to_id: this.to_loc?.id ?? '',
          date: this.date,
          time: this.time,
          passengers: this.passengers,
          luggage:
            !this.luggage || this.luggage === '0' || this.luggage === 'Nessuno'
              ? '0'
              : String(this.luggage),
          childseats: String(parseInt(this.childseats, 10) || 0),
          pets: String(parseInt(this.pets, 10) || 0),
        })
        // Coordinate del punto d'incontro (eventualmente raffinate trascinando il marker)
        this._appendCoord(params, 'from_lat', this.from_loc?.lat)
        this._appendCoord(params, 'from_lng', this.from_loc?.lng)
        this._appendCoord(params, 'to_lat', this.to_loc?.lat)
        this._appendCoord(params, 'to_lng', this.to_loc?.lng)
        this.saveRecentSearch()
        window.location.href = `${this.langPrefix}results?${params.toString()}`
      },

      loadRecentSearches() {
        try {
          const raw = localStorage.getItem('recentSearches')
          this.recentSearches = raw ? JSON.parse(raw) : []
        } catch {
          this.recentSearches = []
        }
      },

      saveRecentSearch() {
        if (!this.from_loc?.id || !this.to_loc?.id) return

        const entry = {
          from: { id: this.from_loc.id, name: this.from_loc.name },
          to: { id: this.to_loc.id, name: this.to_loc.name },
          time: this.time || '',
          ts: Date.now(),
        }

        const MAX = 5
        const existing = this.recentSearches.filter(
          (s) => !(s.from.id === entry.from.id && s.to.id === entry.to.id)
        )
        existing.unshift(entry)
        this.recentSearches = existing.slice(0, MAX)

        try {
          localStorage.setItem(
            'recentSearches',
            JSON.stringify(this.recentSearches)
          )
        } catch {
          /* quota exceeded – ignora */
        }
      },

      applyRecent(item) {
        this.from = item.from.name
        this.to = item.to.name
        this.from_loc = { ...item.from }
        this.to_loc = { ...item.to }
        if (item.time) this.time = item.time
        this.updateMap()
      },

      removeRecent(index) {
        this.recentSearches.splice(index, 1)
        try {
          localStorage.setItem(
            'recentSearches',
            JSON.stringify(this.recentSearches)
          )
        } catch {
          /* ignora */
        }
      },

      /** Aggiunge un parametro coordinata solo se è un numero valido */
      _appendCoord(params, key, value) {
        const n = parseFloat(value)
        if (!Number.isNaN(n)) params.set(key, String(n))
      },

      /** Bounds dell'area servita, parsati da data-search-mbounds */
      _parseMbounds() {
        const raw = this.$el.getAttribute('data-search-mbounds')
        if (!raw) return null
        const [north, east, south, west] = raw.split(',').map(Number)
        if ([north, east, south, west].some(Number.isNaN)) return null
        return { north, east, south, west }
      },

      useMyLocation(field) {
        if (!navigator.geolocation) {
          alert('Il tuo browser non supporta la geolocalizzazione.')
          return
        }

        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude: lat, longitude: lng } = pos.coords
            const bounds = this._parseMbounds()

            if (
              bounds &&
              (lat > bounds.north ||
                lat < bounds.south ||
                lng > bounds.east ||
                lng < bounds.west)
            ) {
              alert(
                "La tua posizione è al di fuori dell'area servita. Seleziona manualmente un punto di partenza."
              )
              return
            }

            const loc = {
              id: `geo:${lat},${lng}`,
              name: 'La mia posizione',
              lat,
              lng,
            }

            this[field + '_loc'] = loc
            this[field] = loc.name
            this.showSuggestions = false

            if (!mapInstance) this.initMap()
            if (mapInstance) {
              if (this.from_loc && this.to_loc) {
                this.updateMap()
              } else {
                mapInstance.showUserPosition(lat, lng, 'La mia posizione')
              }
            }
          },
          (err) => {
            console.error('Geolocation error:', err)
            if (err.code === 1) {
              alert(
                "Permesso di geolocalizzazione negato. Consenti l'accesso alla posizione nelle impostazioni del browser."
              )
            } else {
              alert('Impossibile ottenere la posizione. Riprova.')
            }
          },
          { enableHighAccuracy: true, timeout: 10000 }
        )
      },

      /**
       * Apre/chiude la mappa a schermo intero (overlay CSS, funziona anche su iOS
       * dove la Fullscreen API sui div non è supportata). Dopo il cambio di layout
       * riadatta la mappa al nuovo contenitore.
       */
      toggleMapFullscreen() {
        this.mapFullscreen = !this.mapFullscreen
        document.body.classList.toggle('overflow-hidden', this.mapFullscreen)
        this.$nextTick(() => {
          setTimeout(() => {
            if (mapInstance) mapInstance.refit()
          }, 80)
        })
      },

      updateMap() {
        // Crea la mappa solo al primo utilizzo (prima località selezionata):
        // così non si genera un "map load" fatturato nelle visite in cui il
        // form non viene compilato. Lo script di Maps resta caricato a parte.
        if (!mapInstance) {
          if (!this.from_loc && !this.to_loc) return
          this.initMap()
        }
        if (!mapInstance) return

        if (!this.from_loc || !this.to_loc) {
          this.routeInfo = null
        }

        const from = this.from_loc ? Alpine.raw(this.from_loc) : null
        const to = this.to_loc ? Alpine.raw(this.to_loc) : null

        mapInstance.setRoute(from, to)
      },
    }))
  })
}
